import { useCallback, useEffect, useState } from "react";
import { RequestPromise } from "@hokuto/jam-core";
import { ApiValues } from "../types.js";
import { SpinnerLoading } from "../components/Loading/spinner.js";
import { ErrorLoading } from "../components/Loading/error.js";
import { isCancelablePromise } from "../utils/cancelable-fetch.js";

export function cancelApiRequest(dataRequest: unknown) {
  if (!isCancelablePromise(dataRequest)) return;
  if (dataRequest.isCanceled()) return;
  dataRequest.cancel();
}

export function useApi<T>(
  args: {
    DataLoading?: () => JSX.Element;
    DataError?: () => JSX.Element;
  } = {}
): ApiValues<T> {
  let { DataLoading = SpinnerLoading, DataError = ErrorLoading } = args;

  const [dataRequest, makeDataRequest] = useState<RequestPromise<T>>();

  const setDataRequest = useCallback(
    (current?: RequestPromise<T>) => {
      setError(undefined);
      current
        ?.catch((e) => {
          setError(e);
        })
        .finally(() => {
          makeDataRequest(undefined);
        });
      makeDataRequest((previous) => {
        cancelApiRequest(previous);
        return current;
      });
      return current;
    },
    [makeDataRequest]
  );

  const [error, setError] = useState<Error>();

  useEffect(() => {
    window.addEventListener("beforeunload", () => {
      cancelApiRequest(dataRequest);
    });
    return () => {
      cancelApiRequest(dataRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = !!dataRequest;

  function showComponent(cmp: JSX.Element) {
    if (error) return <DataError />;
    if (loading) return <DataLoading />;
    return cmp;
  }

  return {
    dataRequest,
    setDataRequest,
    showComponent,
    error,
    loading,
  };
}
